import { Check, Clock, Flag } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { CallEvaluationStatus } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { statusTone } from './call-status-tone';

/**
 * The call's traffic-light flag — green / yellow / red.
 *
 * Its own component because more than one surface renders it (the evaluation
 * card and the task-overview last-call strip), and a second hand-rolled pill is
 * precisely the divergence the shared `statusTone` map exists to prevent.
 *
 * `variant="outline"` plus a tone `className` rather than a shadcn variant:
 * there is no `success`/`warning` variant and `components/ui/badge.tsx` must not
 * be modified. Badge composes `cn(badgeVariants({ variant }), className)` and
 * `cn` runs twMerge, so the tone's background cleanly wins over the variant's.
 *
 * The icon and the text carry the same signal as the colour, so the status stays
 * legible to a screen reader and to anyone who can't separate the hues.
 */

const STATUS_LABELS: Record<CallEvaluationStatus, string> = {
  all_clear: 'All clear',
  follow_up: 'Needs follow-up',
  needs_you: 'Needs you',
};

const STATUS_ICONS: Record<CallEvaluationStatus, LucideIcon> = {
  all_clear: Check,
  follow_up: Clock,
  needs_you: Flag,
};

interface CallStatusPillProps {
  status: CallEvaluationStatus;
  className?: string;
}

export function CallStatusPill({ status, className }: CallStatusPillProps): React.ReactElement {
  const tone = statusTone(status);
  const Icon = STATUS_ICONS[status];
  return (
    <Badge variant="outline" className={cn('gap-1 shrink-0', tone?.pill, className)}>
      <Icon className="h-3 w-3" />
      {STATUS_LABELS[status]}
    </Badge>
  );
}
